import { Check, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fullNameOf, useUserSearchQuery, type UserProfile } from '@/features/user';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Avatar,
  Button,
  FormField,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Spinner,
} from '@/shared/ui';
import {
  EMPTY_MEMBER,
  isPairCompetition,
  toParticipantPayload,
  validateSlot,
  type MemberDraft,
} from '../model/participants';
import type { AddParticipantByOrganizerRequest, MatchTypeCode } from '../model/types';

/** Ile milisekund czekamy z wyszukiwaniem po ostatnim naciśnięciu klawisza. */
const SEARCH_DEBOUNCE_MS = 300;

/** Opóźnia frazę, żeby każda litera nie odpalała osobnego zapytania. */
function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

interface MemberFieldsProps {
  label: string;
  value: MemberDraft;
  onChange: (next: MemberDraft) => void;
}

/**
 * Jedna strona slotu: istniejący gracz albo zaproszenie gościa.
 *
 * Backend przyjmuje dokładnie jedną z tych dróg (`ParticipantSpec.validate`),
 * więc przełącznik trybu jest tu, a nie dwa komplety pól naraz.
 */
function MemberFields({ label, value, onChange }: MemberFieldsProps) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<UserProfile | null>(null);
  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const searchQuery = useUserSearchQuery(value.mode === 'existing' ? debouncedSearch : '');

  // Ten sam endpoint dopasowuje też adres e-mail, więc sprawdzenie zajętości
  // nie wymaga osobnego zapytania.
  const debouncedEmail = useDebounced(value.email.trim(), SEARCH_DEBOUNCE_MS);
  const emailQuery = useUserSearchQuery(
    value.mode === 'guest' && debouncedEmail.includes('@') ? debouncedEmail : '',
  );
  const emailOwner =
    emailQuery.data?.find((user) => user.email.toLowerCase() === debouncedEmail.toLowerCase()) ??
    null;

  function pick(user: UserProfile) {
    setPicked(user);
    setSearch('');
    onChange({ ...value, userId: user.id });
  }

  return (
    <fieldset className="flex flex-col gap-4 rounded-xl border border-border p-4">
      <legend className="px-1 text-xs font-semibold tracking-[0.06em] text-fg-muted uppercase">
        {label}
      </legend>

      <SegmentedControl
        label={label}
        fullWidth
        value={value.mode}
        onValueChange={(mode) => {
          setPicked(null);
          setSearch('');
          onChange({ ...EMPTY_MEMBER, mode });
        }}
        options={[
          { value: 'existing', label: 'Gracz Tenify' },
          { value: 'guest', label: 'Nowy gracz' },
        ]}
      />

      {value.mode === 'existing' ? (
        <div className="flex flex-col gap-2">
          {picked ? (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-sunken p-2.5">
              <Avatar name={fullNameOf(picked)} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg-strong">
                {fullNameOf(picked)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPicked(null);
                  onChange({ ...value, userId: null });
                }}
              >
                Zmień
              </Button>
            </div>
          ) : (
            <>
              <Input
                type="search"
                placeholder="Szukaj po imieniu, nazwisku lub e-mailu…"
                aria-label={`Szukaj gracza — ${label}`}
                leadingIcon={<Search />}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />

              {searchQuery.isFetching && (
                <span className="flex items-center gap-2 px-1 text-xs text-fg-subtle">
                  <Spinner size="sm" label={null} /> Szukam…
                </span>
              )}

              {searchQuery.isSuccess && searchQuery.data.length === 0 && (
                <p className="px-1 text-xs text-fg-subtle">
                  Nikogo nie znaleziono. Dodaj gracza jako „Nowy gracz”.
                </p>
              )}

              {searchQuery.data && searchQuery.data.length > 0 && (
                <ul className="max-h-48 overflow-y-auto rounded-lg border border-border">
                  {searchQuery.data.map((user) => (
                    <li key={user.id}>
                      <button
                        type="button"
                        onClick={() => pick(user)}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover',
                          'transition-colors',
                        )}
                      >
                        <Avatar name={fullNameOf(user)} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-fg-strong">
                            {fullNameOf(user)}
                          </span>
                          <span className="block truncate text-xs text-fg-subtle">
                            {user.email}
                          </span>
                        </span>
                        {value.userId === user.id && <Check className="size-4 text-brand-text" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Adres zajęty = ten gracz ma już profil. Backend i tak odrzuci takie
              zgłoszenie, ale lepiej powiedzieć to od razu i pozwolić go wybrać. */}
          {emailOwner && (
            <Alert tone="warning">
              <span className="flex flex-wrap items-center gap-2">
                Gracz {fullNameOf(emailOwner)} ma już konto z tym adresem.
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPicked(emailOwner);
                    onChange({ ...EMPTY_MEMBER, mode: 'existing', userId: emailOwner.id });
                  }}
                >
                  Dodaj tego gracza
                </Button>
              </span>
            </Alert>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Imię" required>
              <Input
                value={value.firstName}
                onChange={(event) => onChange({ ...value, firstName: event.target.value })}
              />
            </FormField>
            <FormField label="Nazwisko" required>
              <Input
                value={value.lastName}
                onChange={(event) => onChange({ ...value, lastName: event.target.value })}
              />
            </FormField>
            <FormField label="E-mail" hint="Pole nieobowiązkowe.">
              <Input
                type="email"
                placeholder="gracz@example.com"
                value={value.email}
                onChange={(event) => onChange({ ...value, email: event.target.value })}
              />
            </FormField>
            <FormField label="Płeć" required>
              <Select
                value={value.gender}
                onChange={(event) =>
                  onChange({ ...value, gender: event.target.value as 'MALE' | 'FEMALE' })
                }
              >
                <option value="MALE">Mężczyzna</option>
                <option value="FEMALE">Kobieta</option>
              </Select>
            </FormField>
          </div>
        </div>
      )}
    </fieldset>
  );
}

export interface AddParticipantDialogProps {
  onClose: () => void;
  /** Typ meczu rozgrywki — decyduje, czy slot wymaga partnera. */
  matchType: MatchTypeCode;
  /** Wywoływane z gotowym ładunkiem dla `participants/by-organizer`. */
  onSubmit: (request: AddParticipantByOrganizerRequest) => void;
  submitting?: boolean;
  /** Błąd z ostatniej próby zapisu — np. „Competition is full”. */
  error?: string | null;
}

/**
 * Okno dodawania zawodnika przez organizatora.
 *
 * Gracz spoza systemu nie wymaga zakładania konta ręcznie: wystarczą e-mail,
 * imię, nazwisko i płeć, a `competition-service` zakłada konto ze statusem
 * INVITED i publikuje event zaproszenia.
 *
 * Rodzic montuje okno dopiero przy otwarciu — dzięki temu stan formularza
 * czyści się sam przy zamknięciu, bez sprzątania w efekcie.
 */
export function AddParticipantDialog({
  onClose,
  matchType,
  onSubmit,
  submitting = false,
  error = null,
}: AddParticipantDialogProps) {
  const pair = isPairCompetition(matchType);
  const [player, setPlayer] = useState<MemberDraft>(EMPTY_MEMBER);
  const [partner, setPartner] = useState<MemberDraft>(EMPTY_MEMBER);
  const [problem, setProblem] = useState<string | null>(null);

  function handleSubmit() {
    const found = validateSlot(player, pair ? partner : null, matchType);
    setProblem(found);
    if (found) return;

    onSubmit({
      player: toParticipantPayload(player),
      ...(pair ? { partner: toParticipantPayload(partner) } : {}),
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={pair ? 'Dodaj parę' : 'Dodaj zawodnika'}
      description={
        pair
          ? 'Para zajmuje jedno miejsce w rozgrywce. Każdą osobę wskaż z bazy Tenify albo zaproś jako nowego gracza.'
          : 'Wskaż gracza z bazy Tenify albo zaproś nowego — dostanie konto i zaproszenie na podany e-mail.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={submitting} leadingIcon={<UserPlus />} onClick={handleSubmit}>
            {pair ? 'Dodaj parę' : 'Dodaj zawodnika'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {(problem ?? error) && <Alert tone="danger">{problem ?? error}</Alert>}

        <MemberFields label="Zawodnik" value={player} onChange={setPlayer} />
        {pair && <MemberFields label="Partner" value={partner} onChange={setPartner} />}
      </div>
    </Modal>
  );
}
